import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileClock, Target, BookOpenCheck, ShieldCheck, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

export default function Audit() {
  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Cumplimiento</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Auditoría y análisis</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Bitácora de acciones, detección de clientes sin visita y productos con baja rotación, grupos contables y configuración de asientos automáticos.</p>
      </div>
      <Tabs defaultValue="aud">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="aud"><FileClock size={14} className="mr-2" />Auditoría</TabsTrigger>
          <TabsTrigger value="rot"><Target size={14} className="mr-2" />Rotación comercial</TabsTrigger>
          <TabsTrigger value="gc"><BookOpenCheck size={14} className="mr-2" />Grupos contables</TabsTrigger>
          <TabsTrigger value="perm" data-testid="tab-perm-audit"><ShieldCheck size={14} className="mr-2" />Auditoría de permisos</TabsTrigger>
        </TabsList>
        <TabsContent value="aud"><Auditoria /></TabsContent>
        <TabsContent value="rot"><Rotacion /></TabsContent>
        <TabsContent value="gc"><GruposContables /></TabsContent>
        <TabsContent value="perm"><PermissionsAudit /></TabsContent>
      </Tabs>
    </div>
  );
}

function Auditoria() {
  const [items, setItems] = useState([]);
  const [filtros, setFiltros] = useState({ modulo: "", accion: "", dias: 30 });
  const load = () => {
    const q = new URLSearchParams();
    if (filtros.modulo) q.set("modulo", filtros.modulo);
    if (filtros.accion) q.set("accion", filtros.accion);
    q.set("dias", filtros.dias);
    api.get(`/core/auditoria?${q.toString()}`).then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-4 gap-3 items-end">
        <div><Label>Módulo</Label><Input value={filtros.modulo} onChange={(e) => setFiltros({ ...filtros, modulo: e.target.value })} placeholder="seguridad, ventas..." /></div>
        <div><Label>Acción</Label><Input value={filtros.accion} onChange={(e) => setFiltros({ ...filtros, accion: e.target.value })} placeholder="crear, editar..." /></div>
        <div><Label>Días</Label><Input type="number" value={filtros.dias} onChange={(e) => setFiltros({ ...filtros, dias: parseInt(e.target.value) || 30 })} /></div>
        <div><Button onClick={load} className="bg-[#205c45] w-full" data-testid="audit-filter-btn">Aplicar filtros</Button></div>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Fecha</th><th>Usuario</th><th>Módulo</th><th>Acción</th><th>Tabla</th><th>Registro</th><th>IP</th></tr></thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id}>
                <td className="text-xs">{a.fecha?.slice(0, 19).replace("T", " ")}</td>
                <td className="font-mono text-xs">{a.usuario_id?.slice(0, 8)}...</td>
                <td className="capitalize"><Badge variant="outline">{a.modulo}</Badge></td>
                <td className="capitalize">{a.accion}</td>
                <td className="font-mono text-xs">{a.tabla}</td>
                <td className="font-mono text-xs">{a.registro_id?.slice(0, 8)}...</td>
                <td className="text-xs text-[#8ba397]">{a.ip || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin eventos. Las acciones futuras del sistema aparecerán aquí.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Rotacion() {
  const [sinVisita, setSinVisita] = useState([]);
  const [bajaRot, setBajaRot] = useState([]);
  const [dias, setDias] = useState(30);
  const load = () => Promise.all([
    api.get(`/core/rotacion/clientes-sin-visita?dias=${dias}`).then(r => setSinVisita(r.data)),
    api.get(`/core/rotacion/productos-baja?dias=${dias * 2}`).then(r => setBajaRot(r.data)),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="mt-4 space-y-5">
      <div className="flex items-end gap-3">
        <div><Label>Días sin actividad</Label><Input type="number" value={dias} onChange={(e) => setDias(parseInt(e.target.value) || 30)} className="w-32" /></div>
        <Button onClick={load} className="bg-[#205c45]" data-testid="rotacion-apply-btn">Aplicar</Button>
      </div>
      <div>
        <h3 className="font-semibold text-[#15241d] mb-2">Clientes sin actividad ({sinVisita.length})</h3>
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
          <table className="erp-table">
            <thead><tr><th>Cliente</th><th>Teléfono</th><th>Email</th><th>Última actividad</th><th className="text-right">Días sin</th></tr></thead>
            <tbody>
              {sinVisita.map(c => (
                <tr key={c.id}>
                  <td className="font-semibold">{c.razon_social}</td>
                  <td>{c.telefono || "—"}</td>
                  <td className="text-xs">{c.email || "—"}</td>
                  <td className="text-xs">{c.ultima_actividad?.slice(0, 10) || "Sin registro"}</td>
                  <td className="text-right tabular-nums">{c.dias_sin_actividad ?? "—"}</td>
                </tr>
              ))}
              {sinVisita.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">Todos los clientes tienen actividad reciente 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-[#15241d] mb-2">Productos con baja rotación ({bajaRot.length})</h3>
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
          <table className="erp-table">
            <thead><tr><th>Código</th><th>Producto</th><th className="text-right">Stock total</th><th>Días sin venta</th></tr></thead>
            <tbody>
              {bajaRot.map(p => (
                <tr key={p.id}>
                  <td className="font-mono text-xs">{p.codigo_interno}</td>
                  <td>{p.nombre_comercial}</td>
                  <td className="text-right tabular-nums font-semibold">{p.stock_total}</td>
                  <td className="text-[#7a2514] text-xs">{p.dias_sin_venta}</td>
                </tr>
              ))}
              {bajaRot.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[#8ba397]">No hay productos estancados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GruposContables() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState([]);
  const [formG, setFormG] = useState({ nombre: "", tipo: "producto", cuenta_principal_id: "", cuenta_secundaria_id: "" });
  const [formC, setFormC] = useState({ tipo_movimiento: "venta", cuenta_debe_id: "", cuenta_haber_id: "", cuenta_impuesto_id: "" });
  const load = () => Promise.all([
    api.get("/core/grupos-contables").then(r => setItems(r.data)),
    api.get("/core/configuracion-contable").then(r => setConfig(r.data)),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const saveGrupo = async () => {
    if (!formG.nombre) return toast.error("Nombre requerido");
    try { await api.post("/core/grupos-contables", formG); toast.success("Creado"); setFormG({ ...formG, nombre: "", cuenta_principal_id: "", cuenta_secundaria_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveConfig = async () => {
    if (!formC.cuenta_debe_id || !formC.cuenta_haber_id) return toast.error("Cuentas requeridas");
    try { await api.post("/core/configuracion-contable", formC); toast.success("Guardada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delG = (g) => api.delete(`/core/grupos-contables/${g.id}`).then(() => { toast.success("Eliminado"); load(); }).catch(e => toast.error(formatApiError(e)));
  return (
    <div className="mt-4 space-y-6">
      <div>
        <h3 className="font-semibold text-[#15241d] mb-2">Grupos contables (Producto / Cliente / Proveedor)</h3>
        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2"><Label>Nombre</Label><Input value={formG.nombre} onChange={(e) => setFormG({ ...formG, nombre: e.target.value })} placeholder="Medicamentos éticos" /></div>
          <div><Label>Tipo</Label><Select value={formG.tipo} onValueChange={(v) => setFormG({ ...formG, tipo: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="producto">Producto</SelectItem><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="proveedor">Proveedor</SelectItem></SelectContent></Select></div>
          <div><Label>Cuenta principal</Label><Input value={formG.cuenta_principal_id} onChange={(e) => setFormG({ ...formG, cuenta_principal_id: e.target.value })} placeholder="4.1.01.001" /></div>
          <div><Button onClick={saveGrupo} className="bg-[#205c45] w-full" data-testid="save-grupo-btn">Crear grupo</Button></div>
        </div>
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-3">
          <table className="erp-table">
            <thead><tr><th>Nombre</th><th>Tipo</th><th>Cuenta principal</th><th>Cuenta secundaria</th><th className="w-12"></th></tr></thead>
            <tbody>
              {items.map(g => (
                <tr key={g.id}>
                  <td className="font-semibold">{g.nombre}</td>
                  <td className="capitalize">{g.tipo}</td>
                  <td className="font-mono text-xs">{g.cuenta_principal_id || "—"}</td>
                  <td className="font-mono text-xs">{g.cuenta_secundaria_id || "—"}</td>
                  <td><Button size="sm" variant="ghost" onClick={() => delG(g)}>×</Button></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">Sin grupos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-[#15241d] mb-2">Configuración contable por tipo de movimiento</h3>
        <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-5 gap-3 items-end">
          <div><Label>Tipo movimiento</Label><Select value={formC.tipo_movimiento} onValueChange={(v) => setFormC({ ...formC, tipo_movimiento: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="venta">Venta</SelectItem><SelectItem value="compra">Compra</SelectItem><SelectItem value="devolucion_cliente">Devolución cliente</SelectItem><SelectItem value="devolucion_proveedor">Devolución proveedor</SelectItem><SelectItem value="ajuste_inventario">Ajuste inventario</SelectItem></SelectContent></Select></div>
          <div><Label>Cuenta Debe</Label><Input value={formC.cuenta_debe_id} onChange={(e) => setFormC({ ...formC, cuenta_debe_id: e.target.value })} placeholder="1.1.02.001" /></div>
          <div><Label>Cuenta Haber</Label><Input value={formC.cuenta_haber_id} onChange={(e) => setFormC({ ...formC, cuenta_haber_id: e.target.value })} placeholder="4.1.01.001" /></div>
          <div><Label>Cuenta Impuesto</Label><Input value={formC.cuenta_impuesto_id} onChange={(e) => setFormC({ ...formC, cuenta_impuesto_id: e.target.value })} placeholder="2.1.08.001" /></div>
          <div><Button onClick={saveConfig} className="bg-[#205c45] w-full" data-testid="save-config-contable-btn">Guardar / Actualizar</Button></div>
        </div>
        <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden mt-3">
          <table className="erp-table">
            <thead><tr><th>Tipo movimiento</th><th>Debe</th><th>Haber</th><th>Impuesto</th></tr></thead>
            <tbody>
              {config.map(c => (
                <tr key={c.id}>
                  <td className="capitalize">{c.tipo_movimiento?.replace("_", " ")}</td>
                  <td className="font-mono text-xs">{c.cuenta_debe_id}</td>
                  <td className="font-mono text-xs">{c.cuenta_haber_id}</td>
                  <td className="font-mono text-xs">{c.cuenta_impuesto_id || "—"}</td>
                </tr>
              ))}
              {config.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[#8ba397]">Sin configuración</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function PermissionsAudit() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [roleFilter, setRoleFilter] = useState("todos");

  useEffect(() => {
    setLoading(true);
    api.get("/users/permisos-audit")
      .then(({ data }) => setItems(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((u) => {
    if (roleFilter !== "todos" && u.role !== roleFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(t) ||
      (u.nombres || "").toLowerCase().includes(t) ||
      (u.apellidos || "").toLowerCase().includes(t) ||
      (u.modulos_raw || []).some((m) => m.toLowerCase().includes(t))
    );
  });

  const toggleExp = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const roles = Array.from(new Set(items.map((i) => i.role))).filter(Boolean).sort();

  return (
    <div className="space-y-4" data-testid="permissions-audit">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Buscar usuario, email o módulo…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="perm-search" />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los roles</SelectItem>
            {roles.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ml-auto">{filtered.length} usuarios</Badge>
      </div>

      <div className="bg-white rounded-md border border-[#d1dbd6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#8ba397]">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#8ba397]">Sin resultados.</div>
        ) : (
          <table className="erp-table">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Usuario</th>
                <th>Rol</th>
                <th className="text-right">Módulos</th>
                <th className="text-right">Submódulos</th>
                <th>Último login</th>
                <th>Creado</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <React.Fragment key={u.id}>
                  <tr className="cursor-pointer hover:bg-[#f7f9f8]" onClick={() => toggleExp(u.id)} data-testid={`perm-row-${u.id}`}>
                    <td>{expanded[u.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td>
                      <div className="font-medium">{u.nombres} {u.apellidos}</div>
                      <div className="text-xs text-[#5c7368]">{u.email}</div>
                    </td>
                    <td><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
                    <td className="text-right tabular-nums">{u.n_modulos}</td>
                    <td className="text-right tabular-nums">{u.n_submodulos}</td>
                    <td className="text-xs text-[#5c7368]">{(u.last_login || "—").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-xs text-[#5c7368]">{(u.created_at || "—").slice(0, 10)}</td>
                    <td>{u.activo ? <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                  </tr>
                  {expanded[u.id] && (
                    <tr>
                      <td colSpan={8} className="bg-[#f7f9f8] p-4">
                        {u.role === "admin" ? (
                          <div className="text-xs text-[#205c45] font-semibold"><ShieldCheck size={12} className="inline mr-1" />Administrador — acceso total a todos los módulos y submódulos</div>
                        ) : u.detalle.length === 0 ? (
                          <div className="text-xs text-[#b53a25]"><AlertTriangle size={12} className="inline mr-1" />Usuario sin módulos asignados</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {u.detalle.map((d) => (
                              <div key={d.modulo} className="bg-white rounded border border-[#d1dbd6] p-2 text-xs">
                                <div className="font-semibold text-[#15241d] uppercase tracking-wider mb-1">{d.modulo === "rrhh" ? "RRHH" : d.modulo}</div>
                                <div className="text-[#5c7368] mb-1"><b>Acciones:</b> {d.acciones.length > 0 ? d.acciones.join(", ") : <i>(ninguna explícita)</i>}</div>
                                <div className="text-[#5c7368]">
                                  <b>Submódulos:</b>{" "}
                                  {d.submodulos.length > 0
                                    ? d.submodulos.map((s) => <span key={s} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 bg-[#e9f2ee] text-[#205c45] rounded text-[10px]">{s}</span>)
                                    : <i>(todos)</i>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-[#5c7368] italic">
        Esta vista refleja los permisos efectivos almacenados en MongoDB (colección <code>users</code>, campo <code>permisos</code>).
        Expanda una fila para ver detalle por módulo y submódulos. Use Seguridad → Usuarios para modificar.
      </p>
    </div>
  );
}
